import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Heart, Car, AlertTriangle, Smartphone, CheckCircle, Users, Shield } from 'lucide-react';
import { practiceAreasEs } from '../mockEs';

const PracticeAreasEs = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-card)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            Áreas de Práctica
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            Representación legal integral para todo tipo de casos de lesiones personales
          </p>
        </div>
      </section>

      {/* Practice Areas List */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '64px' }}>
            {practiceAreasEs.map((area, index) => {
              const IconComponent = {
                'Briefcase': Briefcase,
                'Heart': Heart,
                'Car': Car,
                'AlertTriangle': AlertTriangle,
                'Smartphone': Smartphone,
                'Users': Users,
                'Shield': Shield
              }[area.icon] || Briefcase;

              const isEven = index % 2 === 0;

              return (
                <div
                  key={area.id}
                  className="responsive-grid-2"
                  style={{
                    alignItems: 'center',
                    animation: `fadeIn 0.8s ease-out ${index * 0.1}s backwards`
                  }}
                >
                  {/* Image */}
                  <div
                    style={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '2px solid var(--border-light)',
                      height: '400px'
                    }}
                  >
                    <img
                      src={area.image}
                      alt={area.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <div style={{
                      width: '70px',
                      height: '70px',
                      background: 'var(--brand-primary)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px'
                    }}>
                      <IconComponent size={35} color="var(--text-inverse)" />
                    </div>

                    <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}>
                      {area.title}
                    </h2>

                    <p className="body-medium" style={{ color: 'var(--text-white)', marginBottom: '24px', lineHeight: '1.8' }}>
                      {area.fullDescription}
                    </p>

                    {/* Benefits */}
                    {area.benefits && (
                      <div style={{
                        background: 'var(--bg-card)',
                        padding: '24px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-light)',
                        marginBottom: '24px'
                      }}>
                        <h4 style={{ color: 'var(--brand-primary)', marginBottom: '16px', fontWeight: '600' }}>
                          Lo Que Ofrecemos:
                        </h4>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px'
                        }}>
                          {area.benefits.map((benefit, i) => (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: 'var(--text-white)'
                              }}
                            >
                              <CheckCircle size={18} color="var(--brand-primary)" style={{ flexShrink: 0 }} />
                              <span style={{ fontSize: '0.95rem' }}>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Link
                      to="/es/consulta-gratuita"
                      className="btn-primary"
                    >
                      Obtener Consulta Gratuita
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Results Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
              Resultados Recientes de Casos
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Historial comprobado de asegurar la máxima compensación para nuestros clientes
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              { amount: '$850,000', type: 'Compensación Laboral', description: 'Lesión en sitio de construcción' },
              { amount: '$625,000', type: 'Accidente Automovilístico', description: 'Colisión de múltiples vehículos' },
              { amount: '$475,000', type: 'Resbalón y Caída', description: 'Negligencia en propiedad comercial' },
              { amount: '$390,000', type: 'Accidente de Viaje Compartido', description: 'Acuerdo de accidente de Uber' }
            ].map((result, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--bg-page)',
                  padding: '40px',
                  borderRadius: '16px',
                  border: '2px solid var(--border-light)',
                  textAlign: 'center',
                  animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                }}
              >
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '900',
                  color: 'var(--brand-primary)',
                  marginBottom: '12px'
                }}>
                  {result.amount}
                </div>
                <div style={{
                  color: 'var(--text-white)',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  {result.type}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                  {result.description}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '48px',
            padding: '24px',
            background: 'var(--bg-page)',
            borderRadius: '12px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
              *Los resultados pasados no garantizan resultados futuros. Cada caso es único y se evalúa por sus propios méritos.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreasEs;
